import api, { apiClient } from './api'

export type ConsoleTicket = {
  ticket: string
  port: number
  upid?: string
  password?: string
  websocket_url?: string
  protocol: string
  expires_at?: string
}

export type ConsoleInfo = {
  name: string
  vm_status: string
  console_available: boolean
  supported_protocols: string[]
  default_protocol: string
  serial_available?: boolean
  // `available` é CAPABILITY: "esta VM tem o console", não "há um ticket aberto" (#754).
  // `port`/`ticket` saíram porque o `console/info` deixou de pedir vncproxy/termproxy a
  // cada poll — mantê-los no tipo entregaria `undefined` em silêncio a quem os lesse.
  // Ticket é com o `console/ticket`, que devolve `ConsoleTicket`.
  vnc: {
    available: boolean
    error?: string
  }
  spice: {
    available: boolean
    error?: string
  }
  serial?: {
    available: boolean
    error?: string
  }
  // Resource information
  cpu_cores?: number
  memory_gb?: number
  disk_gb?: number
  uptime?: number
  os_type?: string
  ip_addresses?: string[]
}

export type ConsoleTestResult = {
  vm_running: boolean
  tests: {
    vnc: {
      available: boolean
      port?: number
      ticket_generated?: boolean
      error?: string
    }
    spice: {
      available: boolean
      port?: number
      tls_port?: number
      error?: string
    }
  }
}

export type RdpFileDownload = {
  blob: Blob
  filename: string
}

const parseFilename = (contentDisposition?: string): string => {
  const match = contentDisposition?.match(/filename="?([^"]+)"?/)
  return match?.[1] || 'console.rdp'
}

type ConsoleSession = { session_ticket: string; expires_in: number }

/**
 * Monta a URL do WebSocket do console SEM credencial reutilizável (#1079).
 *
 * O navegador não manda `Authorization` num WebSocket, e o JWT ia em `?token=`: o log de acesso
 * da API gravava a URL inteira. Aqui o JWT sai pelo header de um POST e volta um
 * `session_ticket` opaco, de uso único e 30 s, amarrado ao recurso — é só ele que vai na URL.
 * Cada (re)conexão pede o seu: o ticket é consumido na primeira abertura.
 *
 * Qualquer `token`/`vncticket`/`port` que a URL do servidor ainda traga é descartado: o
 * backend não os lê mais, e ficariam na URL à toa.
 */
export const urlDoSocketComSessao = async (websocketUrl: string): Promise<string> => {
  const url = new URL(websocketUrl)
  const resourceId = url.pathname.split('/').filter(Boolean).pop() ?? ''
  const resourceType = url.searchParams.get('resource_type') === 'gateway' ? 'gateway' : 'vm'

  const sessao = await apiClient.post<ConsoleSession>('/api/v1/console/session', {
    resource_type: resourceType,
    resource_id: resourceId,
  })

  for (const nome of ['token', 'access_token', 'vncticket', 'port', 'session_ticket']) {
    url.searchParams.delete(nome)
  }
  url.searchParams.set('session_ticket', sessao.session_ticket)
  return url.toString()
}

export const consoleApi = {
  // Get console information for a VM instance
  getConsoleInfo: (instanceId: string): Promise<ConsoleInfo> => {
    return apiClient.get(`/api/v1/end-customer/instances/${instanceId}/console/info`)
  },

  // Generate console access ticket
  getConsoleTicket: (
    instanceId: string,
    protocol: 'vnc' | 'spice' | 'serial' = 'vnc',
  ): Promise<ConsoleTicket> => {
    return apiClient.post(
      `/api/v1/end-customer/instances/${instanceId}/console/ticket?protocol=${protocol}`,
    )
  },

  // Test console connectivity
  testConsoleConnection: (instanceId: string): Promise<ConsoleTestResult> => {
    return apiClient.get(`/api/v1/end-customer/instances/${instanceId}/console/test`)
  },

  // Download .rdp file for Windows access via rdpgw
  downloadRdpFile: async (instanceId: string): Promise<RdpFileDownload> => {
    try {
      const response = await api.get<Blob>(`/api/v1/console/${instanceId}/rdp`, {
        responseType: 'blob',
      })

      return {
        blob: response.data,
        filename: parseFilename(response.headers['content-disposition']),
      }
    } catch (error: any) {
      const blob = error?.response?.data
      if (blob instanceof Blob) {
        try {
          const text = await blob.text()
          const payload = JSON.parse(text)
          throw new Error(payload?.detail || 'Erro ao gerar arquivo .rdp', { cause: error })
        } catch {
          throw new Error('Erro ao gerar arquivo .rdp')
        }
      }
      throw error
    }
  },

  // Control VM power state
  controlVM: (instanceId: string, action: 'start' | 'stop' | 'restart' | 'reset'): Promise<any> => {
    return apiClient.post(`/api/v1/end-customer/instances/${instanceId}/action`, { action })
  },
}

export default consoleApi
